/**
 * Ordering and bucket-filtering for the Candidate Requests queue.
 *
 * Pure, and out of RequestsTab for the line ceiling. Requests arrive per MAILBOX, not per role, so a
 * request can have no posting at all (a cold application, or a candidate whose Lever opportunity has
 * no posting) — those collect under one "No role" group rather than being dropped.
 */
import type { InboundRequest } from '../../types/taInboundRequests';

/** How the queue is ordered. Stored in the URL, so a chosen order survives a reload. */
export type RequestSort = 'oldest' | 'newest' | 'relevance';

export const SORT_OPTIONS: { value: RequestSort; label: string }[] = [
  { value: 'oldest', label: 'Oldest first' },
  { value: 'newest', label: 'Newest first' },
  { value: 'relevance', label: 'Needs you first' },
];

export const isRequestSort = (v: unknown): v is RequestSort =>
  v === 'oldest' || v === 'newest' || v === 'relevance';

/**
 * Which summary chip the queue is filtered to. `all` is the unfiltered view.
 *
 * The buckets are the same ones the chips count, so a chip and the list it filters to can never
 * disagree — they are computed from one predicate each.
 */
export type RequestBucket = 'all' | 'drafted' | 'needs_you' | 'no_reply' | 'unread' | 'scheduled';

export const isRequestBucket = (v: unknown): v is RequestBucket =>
  v === 'all' ||
  v === 'drafted' ||
  v === 'needs_you' ||
  v === 'no_reply' ||
  v === 'unread' ||
  v === 'scheduled';

/**
 * A closing thanks needs no reply, and counting it would nag recruiters about mail that needs
 * nothing. A cold-lane item always needs a human: "not in Lever" can mean a real candidate wrote
 * from another address, and its prepared reply would be wrong for them. A FEEDBACK draft is
 * AGG-checked but still read by a person before it goes.
 */
/**
 * A reply is already queued for a later time (decorated onto the item in RequestsTab from the
 * scheduled-sends endpoint — the proxy never puts it on the payload).
 */
export const isScheduled = (i: InboundRequest) => Boolean(i.scheduledSendAt);

/**
 * ⚠️ **Both this and `readyToSend` are gated on `isScheduled`, and both are load-bearing.**
 *
 * `readyToSend` is defined as "not needsAnswer, not noReplyNeeded, has a draft". Gating only
 * `needsAnswer` would therefore push a scheduled item straight into "Ready to send" — the exact
 * wrong label, since the one thing it does not need is sending. A scheduled item belongs to its own
 * bucket and no other.
 */
/**
 * A holding reply acknowledges without answering — UNLESS the proxy swapped its promise of times
 * for the Scheduling tab's actual slots (`proposal.kind === 'proposed'`): that reply answers.
 * ONE predicate for the badge (CardBadges), the card copy and the buckets below, so a chip reading
 * "2 need you" and the cards it filters to cannot disagree about the same item.
 */
export const acknowledgesOnly = (i: InboundRequest) =>
  i.draftSource === 'holding' && i.proposal?.kind !== 'proposed';

export const needsAnswer = (i: InboundRequest) =>
  !isScheduled(i) &&
  i.draftSource !== 'no_reply' &&
  (!i.draft || acknowledgesOnly(i) || i.draftSource === 'feedback' || !i.leverMatched);

export const noReplyNeeded = (i: InboundRequest) => i.draftSource === 'no_reply';

/**
 * Still unread in Gmail — mail the recruiter has not opened.
 *
 * Explicitly `=== true` rather than truthy: the field is optional, and a payload from a proxy that
 * predates it must read as READ. Defaulting the other way would render the whole tab as new.
 */
export const isUnread = (i: InboundRequest) => i.unread === true;

/** Ready to send: a real answer, from Lever, that a human has not been asked to finish. */
export const readyToSend = (i: InboundRequest) =>
  !isScheduled(i) && !needsAnswer(i) && !noReplyNeeded(i) && Boolean(i.draft);

export function inBucket(item: InboundRequest, bucket: RequestBucket) {
  if (bucket === 'all') return true;
  if (bucket === 'scheduled') return isScheduled(item);
  if (bucket === 'needs_you') return needsAnswer(item);
  if (bucket === 'no_reply') return noReplyNeeded(item);
  if (bucket === 'unread') return isUnread(item);
  return readyToSend(item);
}

/**
 * Rank for the "needs you first" order: work, then things to check, then noise.
 *
 * Deliberately not the same as the chip colours — the chips describe state, this describes what to
 * open next.
 */
const relevanceRank = (i: InboundRequest) =>
  // A scheduled item is settled until its time comes, so it ranks with the noise rather than the
  // work — the recruiter has already done what it needed.
  needsAnswer(i) ? 0 : noReplyNeeded(i) || isScheduled(i) ? 2 : 1;

/**
 * Order items INSIDE a role group.
 *
 * **Unread comes first in EVERY mode**, with the chosen sort as the tie-break (Sofia, Aug 20 2026).
 * Mail the recruiter has not opened is the strongest claim on their attention there is, and it is
 * the one signal here that is a fact about the mailbox rather than something this code inferred —
 * so it outranks age and outranks our own guess at what needs a human. The chosen sort then decides
 * the order within each half, so picking "Oldest first" still means oldest first among the unread
 * and oldest first among the read.
 */
export function sortItems(items: InboundRequest[], sort: RequestSort) {
  const byAge = (a: InboundRequest, b: InboundRequest) =>
    sort === 'newest' ? a.ageDays - b.ageDays : b.ageDays - a.ageDays;
  return [...items].sort((a, b) => {
    const unread = Number(isUnread(b)) - Number(isUnread(a));
    if (unread !== 0) return unread;
    if (sort === 'relevance') {
      const rank = relevanceRank(a) - relevanceRank(b);
      // Within a rank the longest-waiting candidate still hurts most.
      if (rank !== 0) return rank;
      return b.ageDays - a.ageDays;
    }
    return byAge(a, b);
  });
}

export function groupByRole(
  visible: InboundRequest[],
  needsAnswerFn: (i: InboundRequest) => boolean,
  sort: RequestSort = 'oldest'
) {
  return Object.values(
    visible.reduce<Record<string, { key: string; roleTitle: string; items: InboundRequest[] }>>(
      (acc, item) => {
        const key = item.postingId || 'no-role';
        acc[key] ??= { key, roleTitle: item.roleTitle || 'No role in Lever', items: [] };
        acc[key].items.push(item);
        return acc;
      },
      {}
    )
  )
    .map((g) => ({
      ...g,
      items: sortItems(g.items, sort),
      needsHuman: g.items.filter(needsAnswerFn).length,
      unreadCount: g.items.filter(isUnread).length,
      oldestDays: Math.max(...g.items.map((i) => i.ageDays)),
      newestDays: Math.min(...g.items.map((i) => i.ageDays)),
    }))
    .sort((a, b) => {
      // Groups follow the same intent as the items: a role holding unread mail rises in every
      // mode, or the unread-first ordering inside the groups would be invisible below a collapsed
      // accordion. Then newest-first flips the age comparison, and "needs you first" puts the
      // roles with unanswered work at the top.
      if (a.unreadCount !== b.unreadCount) return b.unreadCount - a.unreadCount;
      if (sort === 'newest') return a.newestDays - b.newestDays;
      if (sort === 'relevance' && a.needsHuman !== b.needsHuman) return b.needsHuman - a.needsHuman;
      return b.oldestDays - a.oldestDays;
    });
}
